import { Dispatch, createSlice } from "@reduxjs/toolkit";
import { TRequestStatus } from "@models/msc.model";
import productsService from "@/services/products.service";
import { RootState } from "..";
import { TProduct, TProductCategory } from "@/models/products.model";
import { PRODUCT_STATUS_ACTIVE, PRODUCT_STATUS_ARCHIVED } from "@icebergeradmin/common";

type ProductsState = {
  initialized: boolean;
  status: TRequestStatus;
  products: TProduct[];
  categories: TProductCategory[];
};

//-----THUNK FUNCTIONS-----

const initialState: ProductsState = {
  initialized: false,
  status: "IDLE",
  products: [],
  categories: [],
};

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    rollBack(state, actions) {
      state.status = "IDLE";
      state.products = actions.payload;
    },
    gotProducts(state, action) {
      state.products = action.payload;
      state.status = "IDLE";
      state.initialized = true;
    },
    gotCategories(state, action) {
      state.categories = action.payload;
    },
    productSaved(state, action) {
      state.products.push(action.payload);
    },
    productsUpdated(state, action) {
      state.products = state.products.map((t) => (t.id === action.payload.id ? action.payload : t));
    },
    productRemoved(state, action) {
      state.products = state.products.filter((t) => t.id !== action.payload);
    },
    productArchived(state, action) {
      state.products = state.products.map((t) =>
        t.id === action.payload ? { ...t, status: PRODUCT_STATUS_ARCHIVED } : t
      );
    },
    productUnarchived(state, action) {
      state.products = state.products.map((t) =>
        t.id === action.payload ? { ...t, status: PRODUCT_STATUS_ACTIVE } : t
      );
    },
    categorySaved(state, action) {
      state.categories.push(action.payload);
    },
  },
});

// ----------------------------

export const getAllProducts = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { products: { products, initialized } } = getState();
  if (initialized) return products;

  dispatch(productsSlice.actions.startLoading());
  const categories = await productsService.getCategories();
  const response = await productsService.getProducts();
  dispatch(productsSlice.actions.gotCategories(categories));
  dispatch(productsSlice.actions.gotProducts(response));
  return response;
};

// ----------------------------
export const saveProduct =
  (data: Omit<TProduct, "created" | "updated" | "image"> & { image?: string }) => async (dispatch: Dispatch) => {
    const response = await productsService.saveProduct(data);
    dispatch(productsSlice.actions.productSaved(response));
    return response;
  };

export const updateProduct =
  (id: number, data: Omit<TProduct, "created" | "updated" | "image"> & { image?: string }) =>
    async (dispatch: Dispatch) => {
      const response = await productsService.updateProduct(id, data);
      dispatch(productsSlice.actions.productsUpdated(response));
      return response;
    };

export const archiveProduct = (id: number) => async (dispatch: Dispatch) => {
  await productsService.archiveProduct(id);
  dispatch(productsSlice.actions.productArchived(id));
};

export const unarchiveProduct = (id: number) => async (dispatch: Dispatch) => {
  await productsService.unarchiveProduct(id);
  dispatch(productsSlice.actions.productUnarchived(id));
};

// ----------------------------
export const deleteProduct = (id: number) => async (dispatch: Dispatch, getState: () => RootState) => {
  const { products } = getState().products;
  const productsForRollBack = structuredClone(products);

  // optimistic scenario
  dispatch(productsSlice.actions.productRemoved(id));
  try {
    await productsService.deleteProduct(id);
  } catch (error) {
    dispatch(productsSlice.actions.rollBack(productsForRollBack));
    throw error;
  }
};

// ----------------------------
export const saveProductCategory = (name: string) => async (dispatch: Dispatch) => {
  const response = await productsService.saveCategory(name);
  dispatch(productsSlice.actions.categorySaved(response));
  return response;
};
export default productsSlice.reducer;
